import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ModeService } from './mode.service';
import { AuthenticationService } from './authentication.service';
import { AuthorizationService } from './authorization.service';
import { ApiService } from './api.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SecurityDto } from '../dtos/security-dto.model';
import { SecurityUserDto } from '../dtos/security-user-dto.model';
import { User } from 'oidc-client';
import { InactivityService } from './inactivity.service';
import { ReferenceDto } from '../dtos/reference-dto.model';
import { IUITKSelectItemProps } from '@uitk/angular';
import { KeyCodeDto } from '../dtos/key-code-dto.model';
import { PickListDto } from '../dtos/pick-list-dto.model';
import { ZipCodeLookupDto } from '../dtos/zip-code-lookup-dto';
import { Observable, of, Subject } from 'rxjs';
import { ProductDto } from '../dtos/product-dto.model';
import { SpecialitiesDto } from '../dtos/specialities-dto.model';
import { GeozipDto } from '../dtos/geozip-dto.model';
import { Config } from 'protractor';
import { SwalAlert } from '../helpers/alert';
import { finalize, share, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  [key: string]: string | number | boolean | null | object;
  myMSId = '';
  environment = '';
  accessToken = '';
  securityUser: SecurityDto = new SecurityDto();
  user: SecurityUserDto = new SecurityUserDto();

  // Settings from appsettings file.
  apiUrl = '';
  taxApiUrl = '';
  comentUrl = '';
  spaUrl = '';
  accountingApiUrl = '';
  addressStandardizationUrl = '';
  shippingHandlingApiUrl = '';
  salesforceUrl = '';
  discountApiUrl = '';
  idleInterval = 0;
  timeoutInterval = 0
  timeoutInMinutes = 0;
  logger_url = '';
  ccTokenUrl = '';
  linkidx_url = '';
  subscriptionkey_header_value = '';
  subscriptionkey_header_logging_value = '';
  orderEntryFeature = false;
  defaultAccountOwnerCode = '';
  emailFrom = '';
  priceMasterSPAUrl = '';
  quoteCFDPassword = '';
  priceMasterAPIUrl = '';
  orderAttachmentSizeLimit = 0;
  productMasterApiUrl = '';
  productMasterSPAUrl = '';
  profromaInvoicePdfPassword = '';
  enablePeoplesoftApi = false;
  peoplesoftApiUrl = '';
  authorizationApiUrl = '';
  reportsFlag = 1;
  powerbiUrl = '';
  powerWorkspaceId = '';
  commonProxyApiUrl = '';
  xUpstreamEnv = '';
  powerbi_encryptionKey = '';
  powerbi_hmacKey = '';
  orbitUrl = '';
  orbitNewUrl = '';
  geoZipDto: GeozipDto = new GeozipDto();
  geoZipDtoIsReady = new Subject<GeozipDto>();
  specialtiesDto: SpecialitiesDto[] = [];
  getSpecialitiesDtoIsReady = new Subject<SpecialitiesDto>();
  referenceCache!: ReferenceDto | null;
  referenceCachedObservable!: Observable<ReferenceDto> | null;
  environmentCache!: any | null;
  environmentCachedObservable!: Observable<any> | null;
  configCache!: Config | null;
  configCachedObservable!: Observable<Config> | null;
  geoZipCache!: GeozipDto | null;
  geoZipCachedObservable!: Observable<GeozipDto> | null;
  specialitiesCache!: SpecialitiesDto[] | null;
  specialitiesCachedObservable!: Observable<SpecialitiesDto[]> | null;
  zipCodeLookUpCache!: ZipCodeLookupDto[] | null;
  zipCodeLookUpCachedObservable!: Observable<ZipCodeLookupDto[]> | null;
  keyCodeDto: KeyCodeDto = new KeyCodeDto();
  keyCodeCache!: KeyCodeDto | null;
  keyCodeCachedObservable!: Observable<KeyCodeDto> | null;
  keyCodeUpdateCache!: string | null;
  keyCodeUpdateCachedObservable!: Observable<string> | null;
  swalAlert = new SwalAlert();
  
  // Other properties
  reference: ReferenceDto = new ReferenceDto();
  pickLists: PickListDto[] = [];

  private readonly httpClient: HttpClient;

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly authorizationService: AuthorizationService,
    private readonly inactivityService: InactivityService,
    private readonly apiService: ApiService,
    private readonly router: Router,
    private readonly handler: HttpBackend,
    private readonly modeService: ModeService,
    private readonly route: ActivatedRoute
  ) { 
      this.httpClient = new HttpClient(handler);
    }

  // The load method gets called from the app initializer and angular will not let other code run until this promise is complete.
  async load(): Promise<void> {

    // Load the environment file.
    const environmentObject = await this.apiService.get<{ environment: string }>('Loading environment file', 'assets/config/environment.json');
    if (!environmentObject) {
      return;
    }
    this.environment = environmentObject.environment;
    
    // Load the appsettings file.
    const configSettings = await this.apiService.get<ConfigService>('Loading appsettings file', `assets/config/appsettings.${this.environment}.json`);
    if (!configSettings) {
      return;
    }

    // Assign the settings to the service.
    for (const key of Object.keys(configSettings)) {
      if (this.hasOwnProperty(key)) {
        this[key] = configSettings[key];
      }
    }
    
    // Authenticate the user.
    this.authenticationService.initialize(this.linkidx_url, this.spaUrl);
    const state = await this.authenticationService.getState();
    console.log('State:', state);

    if (state === 'Bypass') {
      this.authenticationService.bypass();
      this.myMSId = 'bypass';
      this.user.firstName = 'Bypass';
      this.user.lastName = '';
      this.user.email = 'noone@optum.com';
      this.securityUser.roles = ['SystemAdministrator'];
      
      // Remove extra parameters from authentication
      let firstSlash = window.location.href.indexOf('/', 10);
      if (firstSlash >= 0) {
        let newUrl = window.location.href.substring(firstSlash);
        let state = newUrl.indexOf("state=") - 1;
        if (state >= 0) {
          newUrl = newUrl.substring(0, state);
          this.router.navigateByUrl(newUrl);
        }
      }

      return;
    } else if (state === 'Not authenticated') {
      await this.authenticationService.login();
    } else if (state === 'Authenticated') {
      await this.authenticationService.completeLogin();
      
      let time = localStorage.getItem('startTime');
      let startTime = new Date(time!);    
      
      // Remove extra parameters from authentication
      let firstSlash = window.location.href.indexOf('/', 10);
      if (firstSlash >= 0) {
        let newUrl = window.location.href.substring(firstSlash);
        let state = newUrl.indexOf("state=") - 1;
        if (state >= 0) {
          newUrl = newUrl.substring(0, state);
          this.router.navigateByUrl(newUrl);
        }
      }
    }

    this.inactivityService.setupInactivityTracking();
    this.inactivityService.checkInactivityOnLoad();

    this.authenticationService.userManager.getUser().then((user : User | null) => {
      if (!user?.expired) {
        localStorage.setItem('currentUser', JSON.stringify(user));
      }
    });

    // Get user's security information.
    this.myMSId = this.authorizationService.getMyMSId();
    this.accessToken = this.authorizationService.getAccessToken();
    const isImpersonated = localStorage.getItem('IsImpersonated') === 'true';

    if (!isImpersonated) {
      this.securityUser = await this.authorizationService.getSecurity(this.authorizationApiUrl, this.myMSId) ?? new SecurityDto();
      this.user = this.securityUser.user;
    } else {
      const storedResources = localStorage.getItem('resources');
      if (storedResources) {
        this.securityUser.resources = JSON.parse(storedResources);
      }
      const actualUser = await this.authorizationService.getSecurity(this.authorizationApiUrl, this.myMSId);
      if (actualUser) {
        this.user = actualUser.user;
      }
    }

    // Verify the user has access to the application.
    if (!this.hasResource('TucsonAccess')) {
      alert('You do not have access to this application.');      
      await this.authenticationService.logout();
      await new Promise(f => setTimeout(f, 60000));
    }

    // Load References
    const references = await this.apiService.get<ReferenceDto>('Getting reference data', `${this.apiUrl}/Reference/GetReference`);
    if (references) {
      this.reference = references;
    }
  }

  disablePickListOption(pickList: IUITKSelectItemProps[], value: string): void {
    const optionToDisable = pickList.filter(item => item.value.toLowerCase() === value.toLowerCase())[0];

    if (optionToDisable) {
      optionToDisable.disabled = true;
    }
  }

  hasResource(resource: string): boolean {
    if (localStorage.getItem('bypass')) {
      return true;
    }

    return this.securityUser.resources.filter(r => r.toLowerCase() == resource.toLowerCase()).length > 0;
  }

  getPickList(pickListCode: string): IUITKSelectItemProps[] {
    if (!this.reference) {
      throw new Error("Attempting to get pick list values before refernces are loaded.");
    }

    return this.reference.pickListDtos
      .filter(p => p.pickListCode === pickListCode)
      .map(p => ({ id: p.pickListValue, label: p.description, value: p.pickListValue }));
  }

  getUserEmailFromUserLogin(userLogin: string|null): string {
    if (userLogin) {
      userLogin = userLogin.toLowerCase();

      if (!userLogin.startsWith('ms\\')) {
        userLogin = 'ms\\' + userLogin;
      }

      return this.getPickList('USEREMAIL').filter(x => x.id.toLowerCase() === userLogin)[0]?.label;  
    }
    else {
      return '';
    }
  }

  getDefaultNoteTypeFromUserCode(msId: string): IUITKSelectItemProps {
    const defaultNoteTypeCode = this.getPickList('DNOTETYPE').filter(x => x.id.includes(msId))[0]?.label;
    return this.getPickList("NTYPE").find(item => item.id === defaultNoteTypeCode)!;
  }


  getUserNameUserLogin(userLogin: string | null): string {
    if (userLogin) {
      userLogin = userLogin.toLowerCase();
      if (!userLogin.startsWith('ms\\')) {
        userLogin = 'ms\\' + userLogin;
      }
    return this.getPickList('USERNAMEUSERLOGIN').filter(x => x.id?.toLowerCase() === userLogin?.toLowerCase())[0]?.label;
    }
    else {
      return '';
    }
  }


clearReferenceCache(): void {
  this.referenceCache = null;
  this.referenceCachedObservable=null;
  this.reference = new ReferenceDto();
 }

  public getReferences(): Observable<ReferenceDto> {
    let observable: Observable<ReferenceDto>;

    if (this.referenceCache) {
      observable = of(this.referenceCache);
    }
    else if (this.referenceCachedObservable) {
      observable = this.referenceCachedObservable;
    }
    else {
      this.referenceCachedObservable = this.httpClient.get<ReferenceDto>(`${this.apiUrl}/Reference/GetReference`)
        .pipe(
          tap((response: ReferenceDto) => {
            this.referenceCache = response;
            this.reference = response;
          }),
          share(),
          finalize(() => {
            this.referenceCachedObservable = null;
          })
        );

      observable = this.referenceCachedObservable;
    }

    return observable;
  }

  getUserEmailFromUserCode(userCode: string): string {
    return this.getPickList('USEREMAILUSERCODE').filter(x => x.id.toLowerCase() === userCode.toLowerCase())[0]?.label;

  }

  getFullNameFromUserCode(userCode: string): string {
    return this.getPickList('USERNAME').filter(x => x.id === userCode)[0]?.label;
  }

  getUserPhoneFromUserCode(userCode: string): string {
    return this.getPickList('USERPHONE').filter(x => x.id.toLowerCase() === userCode.toLowerCase())[0]?.label;
  }

  getUserSupervisorFromUserCode(userCode: string): string {
    return this.getPickList('USERSUPERVISOR').filter(x => x.id.toLowerCase() === userCode.toLowerCase())[0]?.label;
  }

  getFaxFromSalesforce(userCode: string): string {
    return this.reference ? userCode !== '' ? this.reference.userFaxDtos.filter(uf => uf.alias === userCode)[0]?.fax : '' : '';
  }

  getPhoneFromCustomerService(accountTypeCode: string): string {
    return this.reference?.customerServiceDtos.filter(cs => cs.accountTypeCode === accountTypeCode)[0].phone!; 
  }

  getFaxFromCustomerService(accountTypeCode: string): string {
    return this.reference?.customerServiceDtos.filter(cs => cs.accountTypeCode === accountTypeCode)[0].fax!;
  }

  getEmailFromCustomerService(accountTypeCode: string): string {
    return this.reference?.customerServiceDtos.filter(cs => cs.accountTypeCode === accountTypeCode)[0].email!;
  }

  getUserCodeFromUserLogin(userLogin: string): string | undefined {
    if (!userLogin) {
      console.error('userLogin is undefined or null');
      return undefined;
    }
  
    userLogin = userLogin.toLowerCase();
    if (!userLogin.startsWith('ms\\')) {
      userLogin = 'ms\\' + userLogin;
    }
  
    return this.getPickList('USERCODE')
      .filter(x => x.id.toLowerCase() === userLogin)[0]?.label;
  }

  getProductDetails(productId: number) {
    return this.reference.productDtos.filter(p => p.id === productId)[0];
  }

  async getProduct(productCode: string): Promise<ProductDto | null> {
    return await this.apiService.get<ProductDto>('getting product from database.', `${this.apiUrl}/reference/product/${productCode}`);
  }

  getLoggedInUserEmail(): string {
    const currentUser = JSON.parse(localStorage.getItem('currentUser') as any);
    return this.user?.email ?? currentUser?.profile?.email ?? '';
  }
  getLoggedInUserName(): string {
    const currentUser = JSON.parse(localStorage.getItem('currentUser') as any);
    return this.user?.fullName ?? currentUser?.profile?.name ?? '';
  }
}